import {skipToken} from '@reduxjs/toolkit/query';

import {ReactNode} from 'react';

import {SourcingCebiaReportSummary, useGetSourcingCebiaReportStatusesQuery} from '@dms/api/vehicle';
import {usePermissions} from '@dms/shared';

import {useInterval} from 'shared/web';

import {SourcingCebiaReportContext} from '../contexts/SourcingCebiaReportContext';

const POLLING_INTERVAL_MS = 10000;

function isPending(report: SourcingCebiaReportSummary | undefined) {
  return report?.status === 'accepted' || report?.status === 'pending';
}

export function SourcingCebiaReportProvider(props: {
  externalIds: string[];
  isEnabled: boolean;
  children: ReactNode;
}) {
  const [canReadReport] = usePermissions({permissionKeys: ['readAutotracerPl']});
  const query = useGetSourcingCebiaReportStatusesQuery(
    props.isEnabled && canReadReport && props.externalIds.length > 0
      ? {externalIds: props.externalIds}
      : skipToken
  );
  const reports = query.data?.reports ?? {};
  const hasPendingReport = Object.values(reports).some(isPending);

  useInterval(() => query.refetch(), hasPendingReport ? POLLING_INTERVAL_MS : null);

  const refresh = () => {
    if (props.isEnabled && canReadReport && props.externalIds.length > 0) {
      query.refetch();
    }
  };

  return (
    <SourcingCebiaReportContext.Provider
      value={{reports, isLoading: query.isLoading, isError: query.isError, canReadReport, refresh}}
    >
      {props.children}
    </SourcingCebiaReportContext.Provider>
  );
}
